// Tenant-scoped Socket.io client. One singleton connection per signed-in
// user; auto-reconnect handles transient network blips, and rotating the
// JWT triggers a hard reconnect with the new token.

import { io, type Socket } from 'socket.io-client';
import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';

let socket: Socket | null = null;
let currentToken: string | null = null;

const baseURL = import.meta.env.VITE_WS_BASE_URL || import.meta.env.VITE_API_BASE_URL || '';

function ensureSocket(token: string): Socket {
  if (socket && currentToken === token) return socket;
  // Token changed → re-open the connection with new auth.
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  currentToken = token;
  socket = io(baseURL || undefined, {
    auth: { token },
    autoConnect: true,
    transports: ['websocket', 'polling'],
  });
  return socket;
}

export function getSocket(): Socket | null {
  const token = useAuthStore.getState().token;
  if (!token) return null;
  return ensureSocket(token);
}

/** Subscribe to a single event for the lifetime of the component. */
export function useSocketEvent<T = unknown>(event: string, handler: (payload: T) => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const s = getSocket();
    if (!s) return;
    const onEvent = (payload: T) => handlerRef.current(payload);
    s.on(event, onEvent);
    return () => {
      s.off(event, onEvent);
    };
  }, [event]);
}

/** Disconnect on logout so the next user opens a fresh socket. */
export function closeSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
    currentToken = null;
  }
}
